import { usePregnancy } from '@/features/emr/api/pregnancies-api';
import { useClinicalEpisodes, useClinicalEvents, usePracticeConfiguration } from '@/features/flowsheets/api';
import { PrenatalHeader } from '@/features/flowsheets/PrenatalHeader';
import { PrenatalGrid } from '@/features/flowsheets/PrenatalGrid';
import { useOrgTimezone } from '@/lib/timezone';
import { withCustomColumns } from '@/features/flowsheets/customDefinitions';
import { prenatalDefinition } from '@/features/flowsheets/definitions';
import { evaluateRules } from '@/features/flowsheets/rules';
import { evaluatePrenatalRules } from '@/features/flowsheets/prenatalRules';
import { formatInstant } from '@/lib/utils/date';
export function PrenatalReference({ pregnancyId, observations }: { pregnancyId: string; observations: boolean }) {
  const pregnancy = usePregnancy(pregnancyId);
  const episodes = useClinicalEpisodes(pregnancyId);
  const episode = episodes.data?.[0];
  const events = useClinicalEvents(episode?.id);
  const configuration = usePracticeConfiguration();
  const { timeZone } = useOrgTimezone();
  if (pregnancy.isError || episodes.isError || events.isError || configuration.isError)
    return <p role="alert">Prenatal information could not be loaded. Reopen this tab to retry.</p>;
  if (!pregnancy.data || episodes.isLoading || configuration.isLoading || (episode && !events.data))
    return <p>Loading prenatal information…</p>;
  const list = events.data ?? [];
  const config = configuration.data;
  const configured = withCustomColumns(
    prenatalDefinition,
    config?.custom_observations ?? [],
    config?.column_labels ?? {},
  );
  const order = config?.column_order ?? [];
  const columns = configured.columns.filter((c) => !config?.hidden_columns.includes(c.key));
  columns.sort(
    (a, b) =>
      (order.indexOf(a.key) < 0 ? order.length : order.indexOf(a.key)) -
      (order.indexOf(b.key) < 0 ? order.length : order.indexOf(b.key)),
  );
  const baseline = pregnancy.data.pre_pregnancy_weight ? pregnancy.data.pre_pregnancy_weight * 453.59237 : null;
  const flags = config
    ? [
        ...evaluateRules(list, config),
        ...evaluatePrenatalRules(list, config, {
          edd: pregnancy.data.edd,
          onDate: formatInstant(new Date().toISOString(), 'yyyy-MM-dd', timeZone),
          timeZone,
          prePregnancyWeightGrams: baseline,
          heightCm: pregnancy.data.height_inches ? pregnancy.data.height_inches * 2.54 : null,
          fetusCount: pregnancy.data.fetus_count,
          active: pregnancy.data.status === 'active' && !episode?.administration?.closed,
        }),
      ]
    : [];

  return observations ? (
    <PrenatalGrid
      definition={{ ...configured, columns }}
      flags={flags}
      prePregnancyWeightGrams={baseline}
      events={list}
      subjects={episode?.subjects ?? []}
      edd={pregnancy.data.edd}
      timeZone={timeZone}
    />
  ) : (
    <PrenatalHeader
      pregnancy={pregnancy.data}
      events={list}
      subjects={episode?.subjects ?? []}
      configuration={configuration.data}
      timeZone={timeZone}
    />
  );
}
